import { all, call, put, takeEvery, take } from 'redux-saga/effects'

import { asApiError, handleError } from 'api/api-utils'
import reportApi from 'api/ReportApi'

import {
  getReportDataError,
  getReportDataSuccess,
  invalidateReportDataCacheError,
  invalidateReportDataCacheSuccess,
  getReportProgressSuccess,
  reportDataWaitingForCalculation,
} from './actions'
import { GET_REPORT_DATA, INVALIDATE_REPORT_DATA_CACHE } from './constants'
import { eventChannel, END } from 'redux-saga'
import {
  readUntilCalculationFinished,
  recordCalculationRefusal,
} from 'utils/actualsCalculationWait'

// Individual exports for testing
let payloadCache = []
export function* getReportData(action) {
  const { companyCode, enableCaching, reportId } = action
  // Keyed by the company and by what the caller wants from the cache, not by the report alone. The
  // same report is read for one company after another as the user moves between them, and with the
  // calculation wait a read can now be in flight for minutes: dropping the next company's read as a
  // duplicate would leave the view waiting on an answer for a company it has already left. A forced
  // uncached re-read - a stale drill-down - must not be swallowed by an ordinary cached read that
  // happens to be in flight either. What the key still does is what it was for: several widgets
  // mounting at once on the same report and company make one request between them.
  const cacheKey = [
    companyCode,
    reportId,
    enableCaching ? 'cached' : 'fresh',
  ].join(':')

  if (payloadCache.includes(cacheKey)) return
  try {
    payloadCache = [...payloadCache, cacheKey]

    yield call(
      readUntilCalculationFinished,
      (outcome) => readReportDataOnce(action, outcome),
      {
        waiting: (companies) =>
          reportDataWaitingForCalculation({ companyCode, reportId, companies }),
        giveUp: (frame) =>
          handleError(asApiError(frame), getReportDataError, {
            reportId,
            companyCode,
          }),
      }
    )
  } catch (error) {
    yield put(handleError(error, getReportDataError, { reportId, companyCode }))
  } finally {
    payloadCache = payloadCache.filter((key) => key !== cacheKey)
  }
}

/**
 * Reads the report once, reporting back by writing into `outcome`:
 * `outcome.calculationInProgress` is set to `{ error, companies }` when the API refused because an
 * actuals calculation is writing the figures of one of the report's companies, which is the
 * caller's cue to ask again, and left unset when the read finished - with data, or with an error
 * that has already been dispatched.
 *
 * It cannot be a return value. The channel is closed with END once the read is over, and redux-saga
 * answers END by *terminating* this generator at that `take` rather than resuming it: nothing after
 * the loop runs, and `call` resolves to undefined. Returning the refusal from here therefore always
 * looked like "nothing to retry" - the report span forever and never asked again.
 */
export function* readReportDataOnce(action, outcome) {
  const { companyCode, customerCode, enableCaching, reportId } = action

  const channel = eventChannel((eventEmmit) => {
    let reportRows = []
    let metadata = null

    const onMessage = (event) => {
      // Skip empty events (keepalive messages etc.)
      if (!event.data || event.data.trim() === '') {
        return
      }

      try {
        const parsed = JSON.parse(event.data)

        // Handle metadata
        if (event.event === 'metadata') {
          metadata = parsed
          return
        }

        // Handle data batches
        if (event.event === 'data-batch') {
          // Backend double-serializes: parse the inner JSON string containing the batch array
          const batchItems = JSON.parse(parsed.items)
          reportRows.push(...batchItems) // Spread to add all items from batch
          return
        }

        // Handle completion
        if (event.event === 'data-complete') {
          eventEmmit(
            getReportDataSuccess({
              report: reportRows,
              // The metadata event is serialized with the API's lower-camel-case resolver, so the
              // key is `metadata`, not `Metadata` - reading the capitalized one left `calculated`
              // permanently undefined.
              calculated: metadata?.metadata?.calculatedUTC,
              exchangeRates: metadata?.metadata?.exchangeRates,
              // Display names of companies whose actuals are mid-calculation while this cached
              // report was served. Empty for a freshly built report, never absent.
              calculationInProgressFor:
                metadata?.metadata?.calculationInProgressFor ?? [],
              reportId,
              companyCode,
            })
          )
          reportRows = []
          metadata = null
          eventEmmit(END)
          return
        }

        // Fallback for old non-streaming format (backward compatibility)
        if (parsed.data) {
          eventEmmit(
            getReportDataSuccess({
              report: parsed.data,
              calculated: parsed.metadata?.calculatedUTC,
              exchangeRates: parsed.metadata?.exchangeRates,
              calculationInProgressFor:
                parsed.metadata?.calculationInProgressFor ?? [],
              reportId,
              companyCode,
            })
          )
          eventEmmit(END)
        }
      } catch (error) {
        eventEmmit(
          handleError(error, getReportDataError, { reportId, companyCode })
        )
        eventEmmit(END)
      }
    }

    const onProgress = (event) => {
      eventEmmit(getReportProgressSuccess({ progress: event.data, ...action }))
    }

    const onError = (event) => {
      const { refused, parsedError } = recordCalculationRefusal(
        event,
        outcome,
        eventEmmit
      )
      if (refused) return

      // Wrapped the way every other error path arrives - ErrorComponent reads props.data, and
      // handing over the parsed body on its own renders a bare "Something went wrong" with
      // neither the server's message nor its code.
      eventEmmit(
        handleError(asApiError(parsedError), getReportDataError, {
          reportId,
          companyCode,
        })
      )
      eventEmmit(END)
    }

    reportApi.getReport({
      companyCode,
      customerCode,
      enableCaching,
      reportId,
      onMessage,
      onProgress,
      onError,
    })

    return () => {}
  })

  // No exit of its own: END terminates this generator where it waits, so the loop is left by
  // termination and never by falling out of it.
  while (true) {
    const event = yield take(channel)
    yield put(event)
  }
}

export function* invalidateReportDataCache(action) {
  try {
    const { reportId } = action
    yield call(reportApi.invalidateReport, { reportId })
    yield put(invalidateReportDataCacheSuccess({ reportId }))
  } catch (error) {
    yield put(handleError(error, invalidateReportDataCacheError))
  }
}

// All sagas to be loaded
export function* reportDatasSaga() {
  yield all([
    takeEvery(GET_REPORT_DATA, getReportData),
    takeEvery(INVALIDATE_REPORT_DATA_CACHE, invalidateReportDataCache),
  ])
}

export default reportDatasSaga
